import { useMemo, useCallback } from "react";
import {
  Text,
  View,
  ActivityIndicator,
  FlatList,
  Pressable,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { Link } from "expo-router";
import { useUserEvents } from "@/features/events/api/event-participants";
import { EventCard } from "@/features/events/components/event-card";
import { toEvent } from "@/features/profile/lib/to-event";
import type { Event } from "@lezz/shared";

interface UserEventsProps {
  userId: string;
}

export function UserEvents({ userId }: UserEventsProps) {
  const { data: events, isLoading } = useUserEvents(userId);

  const allEvents = useMemo(() => {
    if (!events) return [];
    return [...events]
      .sort(
        (a, b) =>
          new Date(b.eventDateAndTime!).getTime() -
          new Date(a.eventDateAndTime!).getTime()
      )
      .map(toEvent);
  }, [events]);

  const renderEventCard = useCallback(
    ({ item: event }: { item: Event }) => (
      <View style={{ marginRight: 12 }}>
        <EventCard event={event} variant="carousel" />
      </View>
    ),
    []
  );

  return (
    <View className="mb-6">
      <View className="mb-3 flex-row items-center justify-between">
        <Text
          className="text-foreground font-display text-xl font-semibold"
          accessibilityRole="header"
        >
          Eventos
        </Text>
        <Link href={`/user/${userId}/events`} asChild>
          <Pressable className="flex-row items-center gap-1 active:opacity-70">
            <Text className="text-foreground text-sm">Ver todos</Text>
            <Ionicons
              name="chevron-forward"
              size={14}
              className="text-muted-foreground"
            />
          </Pressable>
        </Link>
      </View>
      {isLoading ? (
        <ActivityIndicator
          size="small"
          className="text-brand"
          accessibilityLabel="Loading events"
        />
      ) : allEvents.length > 0 ? (
        <FlatList
          data={allEvents}
          keyExtractor={(event) => event.eventId.toString()}
          renderItem={renderEventCard}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingRight: 24 }}
        />
      ) : (
        <Text className="text-muted-foreground text-sm">Sem eventos</Text>
      )}
    </View>
  );
}
